import { memo, useCallback, useState, type JSX } from 'react'

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { pick } from 'lodash-es'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import { useColumns, NewDialog, RowActions } from 'domains/Organizations'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { organizationsOverviewRoute } from 'pages/NWPlatform/Organizations'
import { environmentsOverviewRoute } from 'pages/NWPlatform/Organizations/Organization/Environments'
import { organizationQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const queryClient = useQueryClient()

  const [newDialogOpen, setNewDialogOpen] = useState(false)

  const navigate = useNavigate({ from: organizationsOverviewRoute.fullPath })
  const { query } = organizationsOverviewRoute.useSearch()

  const organizationsQuery = useInfiniteQuery({
    ...organizationQueryKeys.list({ query }),
    select: (data) => ({
      flat: data.pages.flatMap((page) => page.items),
    }),
  })
  const organizations = organizationsQuery.data?.flat

  const columns = useColumns()

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:My.Organization.searchOrganization')}
          onSearch={({ search }) => navigate({ search: { query: search } })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(
            () =>
              queryClient.invalidateQueries({
                queryKey: organizationQueryKeys.list({ query }).queryKey,
              }),
            [query, queryClient],
          )}
          createAction={useCallback(() => setNewDialogOpen(true), [])}
        />
      </ActionStrip>

      <Table
        data={organizations ?? []}
        columns={columns}
        isInitialLoadingGlobal={organizationsQuery.isLoading}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={organizationsOverviewRoute.fullPath}
            to={environmentsOverviewRoute.to}
            params={{ organizationId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        excludeFieldsFromLinking={['id']}
        enableInfinity
        infinityProps={pick(organizationsQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:org.organization', { count: 1 }),
        })}
        RowActions={(props) => <RowActions {...props} />}
      />

      <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
        <NewDialog />
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
